import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  animalsApi, attachmentsApi, calfRecordsApi, cowProfilesApi, customersApi, dairyCollectionHubsApi, lactationsApi,
  milkDailySummaryApi, milkSalesApi, milkSessionsApi,
} from '../../services/resources';
import { formatCurrency, formatDate, formatNumber, shortId, today } from '../../utils/format';
import apiClient, { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { getAppearance } from '../../utils/appearance';
import { useReferenceData } from '../../features/common/referenceData';
import { AnimalTag } from '../../features/common/AnimalTag';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSAttachmentPreview } from '../../components/ILMSAttachmentPreview';
import type {
  Animal, CalfRecord, CalfStatus, CowProfile, Customer, DairyCollectionHub, Lactation, MilkCollectionApprovalStatus,
  MilkDailySummary, MilkSale, MilkSaleCategory, MilkSaleConfirmRequest, MilkSession, MilkSessionType,
} from '../../types';
import type { FieldConfig, ModuleConfig } from '../../features/common/types';
import { MilkDaySheet, MilkShiftsPanel } from './MilkDaySheet';
// Page-vs-tab call: v2 split v1's single Milk_Records into Milk_Sessions (per
// session) + Milk_Daily_Summary (aggregate), and added Lactations/Cow_Profiles
// as their own tables (REQ-007/008). All tabs stay under one "Dairy" nav item
// since they're steps of the same daily-operator workflow (WF-05).
//
// Milk Sessions is the one point of entry for the whole daily routine: the
// milk collected from the herd and the milk sent to the dairy cooperative's
// collection centre are the same real event on this farm, not two — a
// Collection Centre section right inside "Record Session" (optional, blank
// when milk stays entirely on-farm) replaces what was briefly a second,
// separate Collection Centre Slips tab.
//
// Labels throughout this page are bilingual (English / ಕನ್ನಡ) on request —
// bi() below just joins the two; translations are a best effort and worth a
// native-speaker pass if anything reads oddly.
// English / Kannada labels - Kannada can be switched off on Administration -> Appearance.
const bi = (en: string, kn: string) => (getAppearance().show_kannada ? `${en} / ${kn}` : en);

// ---------------------------------------------------------------------------
// Milk Sessions
// ---------------------------------------------------------------------------

// entry_mode is a UI-only field (not sent to the backend) toggling between a
// single animal's own measured yield and a group entry — several animals
// milked/recorded together as one combined litres figure when individual
// yields weren't measured separately (mirrors Coconut's single-tree/group
// harvest toggle, except the group here is a real multi-select of animals,
// not a free-text label, since each one still needs its own daily total).
/** The shift being milked right now: Morning until 12 noon, Evening after. */
const currentShift = (): MilkSessionType => (new Date().getHours() >= 12 ? 'Evening' : 'Morning');

const isGroupEntry = (v: Record<string, unknown>) => v.entry_mode === 'group';
const isSingleEntry = (v: Record<string, unknown>) => !isGroupEntry(v);
const isCreating = (v: Record<string, unknown>) => v.id === undefined;

// Resolves animal_id/group_animal_ids to their Tag ID (from the same
// 'animals' reference list the Animal picker itself uses, cached by
// react-query — no extra request per row) instead of the raw UUID
// shortId() used to show, which nobody entering a slip can read back.
function AnimalCell({ session }: { session: MilkSession }) {
  const { data } = useReferenceData(['animals']);
  const labelFor = (id: string) => data.animals.find((a) => a.value === id)?.label ?? shortId(id);

  if (session.animal_id) return <span>{labelFor(session.animal_id)}</span>;

  const ids = session.group_animal_ids ?? [];
  const labels = ids.map(labelFor);
  // Full list, comma-separated, wrapping onto further lines as needed
  // (on request) — every animal in a batch should be readable straight off
  // the table, not hidden behind a "+N more" that needs a click to expand.
  return <span className="whitespace-normal break-words">{labels.join(', ') || '—'}</span>;
}

// The photo the reviewer checks the figures against, right where they
// approve - or a clear note when the agent's photo never arrived (the
// entry can still be opened and the photo added before approving).
function ProofPhoto({ attachmentId, required }: { attachmentId?: string | null; required?: boolean }) {
  if (attachmentId) return <ILMSAttachmentPreview attachmentId={attachmentId} label="Photo" />;
  if (!required) return null;
  return <span className="text-helper-text text-warning-700">No photo yet — open the entry to add it, then approve.</span>;
}

function SessionApprovalCell({ session }: { session: MilkSession }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canApprove = can('Milk', 'Approve');
  const mutation = useMutation({
    mutationFn: (status: MilkCollectionApprovalStatus) => milkSessionsApi.updateApproval(session.id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['milk-sessions'] }),
  });

  if (session.approval_status !== 'Pending' || !canApprove) {
    return <ILMSStatusBadge status={session.approval_status} size="sm" />;
  }
  return (
    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
      <ProofPhoto attachmentId={session.photo_attachment_id} />
      <div className="flex items-center gap-2">
        <ILMSStatusBadge status={session.approval_status} size="sm" />
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('Approved')} className="text-helper-text text-success-700 hover:underline disabled:opacity-50">
          Approve
        </button>
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('Rejected')} className="text-helper-text text-critical-600 hover:underline disabled:opacity-50">
          Reject
        </button>
      </div>
      {/* A session created before the photo was made mandatory won't have
          one — the backend refuses to approve it without one, surfaced here
          rather than failing silently. */}
      {mutation.isError && (
        <span className="text-helper-text text-critical-600">{getApiErrorMessage(mutation.error, 'Unable to update approval status.')}</span>
      )}
    </div>
  );
}

// Milk Session is collection ONLY (unified on request - see
// app/models/dairy.py's MilkSession docstring): litres/fat%/SNF% off the
// herd, a required verification photo. Where that milk then goes - a
// Dairy Collection Hub or a Customer/Vendor/Partner - is entirely the
// Milk Sales tab's job now (buildMilkSaleConfig below), regardless of
// destination, so a session no longer carries a Rate/Amount/hub section
// of its own.
function buildMilkConfig(): ModuleConfig<MilkSession> {
  return {
  title: bi('Milk Sessions', 'ಹಾಲು ಅಧಿವೇಶನಗಳು'), entityLabel: 'milk session', module: 'Milk', queryKey: 'milk-sessions', idKey: 'id',
  api: {
    list: milkSessionsApi.list,
    create: async (v: any) => {
      const created = await milkSessionsApi.create({
        production_date: v.production_date, session_type: v.session_type, milking_time: v.milking_time,
        litres: Number(v.litres), fat_percent: Number(v.fat_percent),
        snf_percent: v.snf_percent !== '' && v.snf_percent != null ? Number(v.snf_percent) : undefined,
        remarks: v.remarks || undefined,
        ...(v.entry_mode === 'group' ? { group_animal_ids: v.group_animal_ids } : { animal_id: v.animal_id }),
      } as any);
      // The session must exist before a photo can be attached to it
      // (Attachments are keyed by entity_id) — same two-step pattern as
      // Animal photos. A photo is mandatory for every session (an Admin
      // needs it to approve), so a failed upload here can't be silently
      // swallowed the way it used to be — that previously hid a real
      // permission gap (Operators lacked Attachments access entirely) as
      // what looked like a successful save with no photo, forever. Roll
      // the just-created session back and surface the real error instead
      // of leaving an incomplete, unapprovable record behind.
      if (v.photo_file) {
        try {
          const attachment = await attachmentsApi.upload(v.photo_file as File, 'MilkSession', created.id);
          return await milkSessionsApi.setPhoto(created.id, attachment.id);
        } catch (err) {
          await milkSessionsApi.remove(created.id).catch(() => {});
          throw new Error(getApiErrorMessage(err, 'The session could not be saved — the photo failed to upload.'));
        }
      }
      return created;
    },
    update: async (id: string, v: any) => {
      let updated = await milkSessionsApi.update(id, {
        production_date: v.production_date, session_type: v.session_type,
        litres: Number(v.litres), fat_percent: Number(v.fat_percent),
        snf_percent: v.snf_percent !== '' && v.snf_percent != null ? Number(v.snf_percent) : undefined,
        milking_time: v.milking_time,
        remarks: v.remarks || undefined,
      } as any);
      if (v.photo_file) {
        try {
          const attachment = await attachmentsApi.upload(v.photo_file as File, 'MilkSession', id);
          updated = await milkSessionsApi.update(id, { photo_attachment_id: attachment.id } as any);
        } catch (err) {
          // The session's other field edits above already saved — only the
          // photo itself failed to attach — but that still has to reach the
          // user, not disappear, since approval depends on it being there.
          throw new Error(getApiErrorMessage(err, 'The session was saved, but the photo failed to upload — try again.'));
        }
      }
      return updated;
    },
    remove: milkSessionsApi.remove,
  },
  canDelete: true,
  referenceKeys: ['animals'], addLabel: 'Record Session',
  searchPlaceholder: 'Search by session ID…',
  // Worked out each time the form opens - Evening from 12 noon.
  defaultValues: () => ({
    production_date: today(), session_type: currentShift(), milking_time: new Date().toISOString(), entry_mode: 'single',
    snf_percent: 0,
  }) as unknown as Partial<MilkSession>,
  // entry_mode has no real backing column — opening an existing session
  // for edit has to infer it from its actual data (animal_id vs
  // group_animal_ids) so the right picker starts visible.
  deriveEditValues: (row) => ({
    entry_mode: row.animal_id ? 'single' : 'group',
  }),
  columns: [
    { key: 'date', header: bi('Date', 'ದಿನಾಂಕ'), primary: true, render: (r) => formatDate(r.production_date) },
    { key: 'code', header: bi('Session ID', 'ಅಧಿವೇಶನ ಐಡಿ'), render: (r) => r.session_code ?? '—' },
    { key: 'animal', header: bi('Animal', 'ಪ್ರಾಣಿ'), render: (r) => <AnimalCell session={r} /> },
    { key: 'session', header: bi('Session', 'ಸೆಷನ್'), render: (r) => r.session_type },
    { key: 'litres', header: bi('Litres', 'ಲೀಟರ್'), align: 'right', render: (r) => formatNumber(r.litres, 2) },
    { key: 'fat', header: bi('Fat %', 'ಕೊಬ್ಬು %'), align: 'right', render: (r) => formatNumber(r.fat_percent, 2), hideOnTablet: true },
    { key: 'status', header: bi('Status', 'ಸ್ಥಿತಿ'), render: (r) => <SessionApprovalCell session={r} /> },
    { key: 'sale', header: bi('Sold Via', 'ಮಾರಾಟ'), hideOnTablet: true, render: (r) => (
      r.allocated_sale_code
        ? <span className="text-ink-700">{r.allocated_sale_code}</span>
        : <span className="text-helper-text text-warning-700">Pending sale</span>
    ) },
  ],
  fields: [
    { name: 'entry_mode', label: bi('Entry Type', 'ನಮೂದು ಪ್ರಕಾರ'), type: 'select', section: bi('Session', 'ಅಧಿವೇಶನ'),
      required: isCreating, visibleWhen: isCreating, staticOptions: [
        { value: 'single', label: bi('Single Animal', 'ಒಂದು ಪ್ರಾಣಿ') },
        { value: 'group', label: bi('Batch of Animals', 'ಪ್ರಾಣಿಗಳ ಗುಂಪು') },
      ], helperText: 'Batch when you only have one combined litres figure for several animals milked together — no individual yields. Pick this first; the rest of the form follows from it.' },
    { name: 'animal_id', label: bi('Animal', 'ಪ್ರಾಣಿ'), type: 'select', section: bi('Session', 'ಅಧಿವೇಶನ'),
      required: (v) => isCreating(v) && isSingleEntry(v), visibleWhen: (v) => isCreating(v) && isSingleEntry(v), optionsKey: 'animals' },
    { name: 'group_animal_ids', label: bi('Animals (Batch)', 'ಪ್ರಾಣಿಗಳು (ಗುಂಪು)'), type: 'multiselect', section: bi('Session', 'ಅಧಿವೇಶನ'),
      required: (v) => isCreating(v) && isGroupEntry(v), visibleWhen: (v) => isCreating(v) && isGroupEntry(v), optionsKey: 'animals',
      helperText: 'Pick 2 or more animals — use "Select all" in the dropdown for the whole herd at once, or search to narrow it down first. The litres figure below is the combined total, split evenly across each one’s own daily total.' },

    // Editable on an existing entry too - to move one saved under the wrong shift / day.
    { name: 'production_date', label: bi('Date', 'ದಿನಾಂಕ'), type: 'date', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), required: true },
    { name: 'session_type', label: bi('Shift', 'ಶಿಫ್ಟ್'), type: 'select', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), required: true, staticOptions: [
      { value: 'Morning', label: bi('Morning', 'ಬೆಳಗ್ಗೆ') }, { value: 'Evening', label: bi('Evening', 'ಸಂಜೆ') }, { value: 'Third', label: bi('Third', 'ಮೂರನೇ') },
    ] },
    { name: 'litres', label: bi('Quantity (L)', 'ಪ್ರಮಾಣ (ಲಿ)'), type: 'decimal', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), required: true, min: 0,
      helperText: 'For a batch entry, this is the combined total across every selected animal.' },
    { name: 'fat_percent', label: bi('Fat %', 'ಕೊಬ್ಬು %'), type: 'decimal', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), optional: true, min: 0 },
    { name: 'snf_percent', label: 'SNF %', type: 'decimal', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), optional: true, min: 0,
      helperText: 'Not on the collection hub slip — leave blank unless you actually have this figure.' },
    { name: 'remarks', label: bi('Remarks', 'ಸೂಚನೆ'), type: 'textarea', section: bi('Session Details', 'ಅಧಿವೇಶನ ವಿವರಗಳು'), optional: true, fullWidth: true,
      helperText: 'Anything unusual about this session the figures above don\'t already explain — e.g. "12L spilled while carrying the can to the hub".' },
    { name: 'photo_file', label: bi('Photo', 'ಫೋಟೋ'), type: 'file', section: bi('Verification', 'ಪರಿಶೀಲನೆ'),
      optional: true, existingAttachmentKey: 'photo_attachment_id',
      helperText: 'Optional — a photo of the slip/session helps the Admin check the figures before approving.' },
  ] as FieldConfig<any>[],
  };
}

const summaryConfig: ModuleConfig<MilkDailySummary> = {
  title: bi('Daily Summary', 'ದೈನಂದಿನ ಸಾರಾಂಶ'), entityLabel: 'summary', module: 'Milk', queryKey: 'milk-daily-summary', api: { list: milkDailySummaryApi.list, create: async () => { throw new Error('read-only'); }, update: async () => { throw new Error('read-only'); } }, idKey: 'id',
  readOnly: true, exportDateKey: 'summary_date',
  // One row per animal per day - a batch session splits its litres across
  // every animal in it, so the Animal column is what tells those rows apart.
  columns: [
    { key: 'date', header: bi('Date', 'ದಿನಾಂಕ'), primary: true, render: (r) => formatDate(r.summary_date) },
    { key: 'animal', header: bi('Animal', 'ಪ್ರಾಣಿ'), render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'total', header: bi('Total Litres', 'ಒಟ್ಟು ಲೀಟರ್'), align: 'right', render: (r) => formatNumber(r.total_litres, 2) },
    { key: 'sessions', header: bi('Sessions', 'ಅಧಿವೇಶನಗಳು'), render: (r) => r.session_count ?? '—' },
    { key: 'fat', header: bi('Avg Fat %', 'ಸರಾಸರಿ ಕೊಬ್ಬು %'), render: (r) => formatNumber(r.avg_fat_percent, 1), hideOnTablet: true },
    { key: 'snf', header: 'Avg SNF %', render: (r) => formatNumber(r.avg_snf_percent, 1), hideOnTablet: true },
  ],
  fields: [],
};

const dairyCollectionHubsConfig: ModuleConfig<DairyCollectionHub> = {
  title: bi('Dairy Collection Hubs', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರಗಳು'), entityLabel: 'collection hub', module: 'Milk', queryKey: 'dairy-collection-hubs', api: dairyCollectionHubsApi, idKey: 'id',
  canDelete: true, addLabel: 'Add Hub', defaultValues: { active: true, is_default: false },
  columns: [
    { key: 'name', header: bi('Name', 'ಹೆಸರು'), primary: true, render: (r) => r.name },
    { key: 'code', header: bi('Code / Number', 'ಕೋಡ್ / ಸಂಖ್ಯೆ'), render: (r) => r.hub_code || '—' },
    { key: 'contact', header: bi('Contact', 'ಸಂಪರ್ಕ'), render: (r) => r.contact || '—' },
    { key: 'default', header: bi('Default', 'ಡೀಫಾಲ್ಟ್'), render: (r) => (r.is_default ? <ILMSStatusBadge status="Default" tone="success" size="sm" /> : '—') },
    { key: 'active', header: bi('Active', 'ಸಕ್ರಿಯ'), render: (r) => (r.active ? 'Active' : 'Inactive') },
  ],
  fields: [
    { name: 'name', label: bi('Name', 'ಹೆಸರು'), type: 'text', section: bi('Details', 'ವಿವರಗಳು'), required: true },
    { name: 'hub_code', label: bi('Code / Number', 'ಕೋಡ್ / ಸಂಖ್ಯೆ'), type: 'text', section: bi('Details', 'ವಿವರಗಳು'), optional: true,
      helperText: 'The co-op/society\'s own code for this hub, if it has one — shown alongside the name wherever you pick a hub, including on the Add Milk Session form.' },
    { name: 'contact', label: bi('Contact', 'ಸಂಪರ್ಕ'), type: 'text', section: bi('Details', 'ವಿವರಗಳು'), optional: true },
    { name: 'address', label: bi('Address', 'ವಿಳಾಸ'), type: 'textarea', section: bi('Details', 'ವಿವರಗಳು'), optional: true, fullWidth: true },
    { name: 'is_default', label: bi('Default Hub', 'ಡೀಫಾಲ್ಟ್ ಕೇಂದ್ರ'), type: 'checkbox', section: bi('Details', 'ವಿವರಗಳು'),
      helperText: 'Pre-fills this hub on every new milk session sent to a collection hub. Setting this unsets any other default.' },
    { name: 'active', label: bi('Active', 'ಸಕ್ರಿಯ'), type: 'checkbox', section: bi('Details', 'ವಿವರಗಳು') },
  ],
};

const lactationsConfig: ModuleConfig<Lactation> = {
  title: bi('Lactations', 'ಹಾಲು ಅವಧಿಗಳು'), entityLabel: 'lactation', module: 'Milk', queryKey: 'lactations', api: lactationsApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { status: 'Active', start_date: today() },
  columns: [
    { key: 'animal', header: bi('Animal', 'ಪ್ರಾಣಿ'), primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'number', header: '#', render: (r) => r.lactation_number },
    { key: 'start', header: bi('Start', 'ಪ್ರಾರಂಭ'), render: (r) => formatDate(r.start_date) },
    { key: 'dry_off', header: bi('Dry-off', 'ಒಣಗಿಸುವಿಕೆ'), render: (r) => formatDate(r.dry_off_date) },
    { key: 'total', header: bi('Total (L)', 'ಒಟ್ಟು (ಲೀ)'), align: 'right', render: (r) => formatNumber(r.total_milk_litres, 1) },
    { key: 'status', header: bi('Status', 'ಸ್ಥಿತಿ'), render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'animal_id', label: bi('Animal', 'ಪ್ರಾಣಿ'), type: 'select', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), required: true, optionsKey: 'animals' },
    { name: 'lactation_number', label: bi('Lactation Number', 'ಹಾಲು ಅವಧಿ ಸಂಖ್ಯೆ'), type: 'number', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), required: true, min: 1 },
    { name: 'start_date', label: bi('Start Date', 'ಪ್ರಾರಂಭ ದಿನಾಂಕ'), type: 'date', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), required: true },
    { name: 'dry_off_date', label: bi('Dry-off Date', 'ಒಣಗಿಸುವ ದಿನಾಂಕ'), type: 'date', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), optional: true },
    { name: 'end_date', label: bi('End Date', 'ಅಂತ್ಯ ದಿನಾಂಕ'), type: 'date', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), optional: true },
    { name: 'status', label: bi('Status', 'ಸ್ಥಿತಿ'), type: 'select', section: bi('Lactation', 'ಹಾಲು ಅವಧಿ'), required: true, staticOptions: [
      { value: 'Active', label: bi('Active', 'ಸಕ್ರಿಯ') }, { value: 'DriedOff', label: bi('Dried Off', 'ಒಣಗಿದೆ') }, { value: 'Completed', label: bi('Completed', 'ಪೂರ್ಣಗೊಂಡಿದೆ') },
    ] },
  ],
};

const cowProfilesConfig: ModuleConfig<CowProfile> = {
  title: bi('Cow Profiles', 'ಹಸು ಪ್ರೊಫೈಲ್‌ಗಳು'), entityLabel: 'cow profile', module: 'Milk', queryKey: 'cow-profiles', api: cowProfilesApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { current_status: 'Dry' },
  columns: [
    { key: 'animal', header: bi('Animal', 'ಪ್ರಾಣಿ'), primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'lactation', header: bi('Lactation #', 'ಹಾಲು ಅವಧಿ #'), render: (r) => r.lactation_number ?? '—' },
    { key: 'status', header: bi('Status', 'ಸ್ಥಿತಿ'), render: (r) => <ILMSStatusBadge status={r.current_status} size="sm" /> },
    { key: 'calvings', header: bi('Calvings', 'ಕರು ಹಾಕಿದ ಸಂಖ್ಯೆ'), render: (r) => r.calving_count ?? 0 },
    { key: 'last_calving', header: bi('Last Calving', 'ಕೊನೆಯ ಕರು'), render: (r) => formatDate(r.last_calving_date), hideOnTablet: true },
    // Computed on read - the Animal Register columns.
    { key: 'dim', header: bi('Days in Milk', 'ಹಾಲಿನ ದಿನಗಳು'), align: 'right', render: (r) => r.days_in_milk ?? '—' },
    { key: 'stage', header: bi('Breeding', 'ಸಂತಾನೋತ್ಪತ್ತಿ'), render: (r) => r.breeding_stage || '—' },
    { key: 'expected', header: bi('Expected Calving', 'ನಿರೀಕ್ಷಿತ ಕರು'), render: (r) => formatDate(r.expected_calving_date), hideOnTablet: true },
    { key: 'avg_7d', header: bi('Avg Milk/Day, 7d (L)', 'ಸರಾಸರಿ ಹಾಲು/ದಿನ (ಲೀ)'), align: 'right', render: (r) => formatNumber(r.avg_milk_last_7_days, 1) },
    { key: 'avg_yield', header: bi('Avg Yield (L)', 'ಸರಾಸರಿ ಉತ್ಪಾದನೆ (ಲೀ)'), align: 'right', render: (r) => formatNumber(r.average_daily_yield_litres, 1), hideOnTablet: true },
  ],
  fields: [
    { name: 'animal_id', label: bi('Animal', 'ಪ್ರಾಣಿ'), type: 'select', section: bi('Profile', 'ಪ್ರೊಫೈಲ್'), required: true, optionsKey: 'animals' },
    { name: 'lactation_number', label: bi('Lactation Number', 'ಹಾಲು ಅವಧಿ ಸಂಖ್ಯೆ'), type: 'number', section: bi('Profile', 'ಪ್ರೊಫೈಲ್'), optional: true, min: 0 },
    { name: 'current_status', label: bi('Status', 'ಸ್ಥಿತಿ'), type: 'select', section: bi('Profile', 'ಪ್ರೊಫೈಲ್'), required: true, staticOptions: [
      { value: 'Dry', label: 'Dry' }, { value: 'Pregnant', label: 'Pregnant' }, { value: 'Lactating', label: 'Lactating' },
      { value: 'PregnantLactating', label: 'Pregnant & Lactating' }, { value: 'PregnantDry', label: 'Pregnant & Dry' },
      { value: 'Sick', label: 'Sick' }, { value: 'Sold', label: 'Sold' }, { value: 'Returned', label: 'Returned to Supplier' }, { value: 'Dead', label: 'Dead' },
    ] },
    { name: 'calving_count', label: bi('Lifetime Calvings', 'ಜೀವಿತಾವಧಿ ಕರು ಸಂಖ್ಯೆ'), type: 'number', section: bi('Profile', 'ಪ್ರೊಫೈಲ್'), optional: true, min: 0 },
    { name: 'last_calving_date', label: bi('Last Calving Date', 'ಕೊನೆಯ ಕರು ಹಾಕಿದ ದಿನಾಂಕ'), type: 'date', section: bi('Profile', 'ಪ್ರೊಫೈಲ್'), optional: true },
  ],
};

// Age in whole days since birth — a pure display figure, not stored (would
// go stale the moment it's saved), so it's computed fresh every render.
function ageInDays(birthDate: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(birthDate).getTime()) / 86_400_000));
}

const CALF_STATUSES: { value: CalfStatus; label: string }[] = [
  { value: 'OnFarm', label: 'On the farm' }, { value: 'Sold', label: 'Sold' },
  { value: 'Dead', label: 'Dead' }, { value: 'GivenAway', label: 'Given away' },
];
const CALF_STATUS_LABEL = Object.fromEntries(CALF_STATUSES.map((s) => [s.value, s.label])) as Record<CalfStatus, string>;
// Source is only picked when adding - an existing record keeps its own.
const isBoughtCalf = (v: Record<string, unknown>) => v.source === 'Purchased';
const isBornHere = (v: Record<string, unknown>) => !isBoughtCalf(v);

// Calf Records is a lightweight, same-day birth log (on request) - dam is a
// real Animal, but the calf itself is just gender/color/tag/weight fields
// here, not a full Animal record, so logging a birth doesn't first require
// walking through Add Animal. Mother-side columns (breed/location/farm/
// purchase date/tag) are read straight off the Animal record already loaded
// for the Dam/Sire pickers - no extra backend endpoint needed for those.
function buildCalfRecordsConfig(damsById: Map<string, Animal>): ModuleConfig<CalfRecord> {
  return {
  title: bi('Calf Records', 'ಕರು ದಾಖಲೆಗಳು'), entityLabel: 'calf record', module: 'Milk', queryKey: 'calf-records', api: calfRecordsApi, idKey: 'id',
  canDelete: true,
  referenceKeys: ['animals', 'suppliers'], defaultValues: { birth_date: today(), source: 'BornInFarm', status: 'OnFarm' },
  // Compact on purpose (on request: line up like the other tables) - the
  // calf in one cell (sex, born/bought, name), the mother in one cell (name
  // + tag); farm code / location stay on the mother's own record.
  columns: [
    { key: 'sl', header: 'Sl', hideOnMobile: true, className: 'w-10', render: (_r, i) => i + 1 },
    { key: 'calf', header: bi('Calf', 'ಕರು'), primary: true, sortValue: (r) => r.calf_name ?? '', render: (r) => (
      <span className="flex flex-col whitespace-nowrap">
        <span className="font-medium">{r.calf_name || 'No name'}</span>
        <span className="text-helper-text text-ink-500">{r.calf_gender ?? '—'} · {r.source === 'Purchased' ? 'Bought' : 'Born here'}</span>
      </span>
    ) },
    { key: 'status', header: bi('Status', 'ಸ್ಥಿತಿ'), render: (r) => (
      <ILMSStatusBadge status={CALF_STATUS_LABEL[r.status] ?? r.status} tone={r.status === 'OnFarm' ? 'success' : r.status === 'Dead' ? 'critical' : 'neutral'} size="sm" />
    ) },
    { key: 'birth', header: bi('Born', 'ಜನನ'), sortValue: (r) => r.birth_date, className: 'whitespace-nowrap', render: (r) => formatDate(r.birth_date) },
    { key: 'age', header: bi('Age (days)', 'ವಯಸ್ಸು'), align: 'right', sortValue: (r) => r.birth_date, render: (r) => ageInDays(r.birth_date) },
    { key: 'color', header: bi('Colour', 'ಬಣ್ಣ'), className: 'whitespace-nowrap', render: (r) => r.calf_color || '—' },
    { key: 'weight', header: bi('Weight (kg)', 'ತೂಕ'), align: 'right', render: (r) => formatNumber(r.birth_weight_kg, 1) },
    { key: 'calf_tag', header: bi('Calf Tag', 'ಟ್ಯಾಗ್'), className: 'whitespace-nowrap', render: (r) => r.calf_tag_no || '—' },
    { key: 'mother', header: bi('Mother', 'ತಾಯಿ'), className: 'whitespace-nowrap', render: (r) => {
      const dam = r.dam_animal_id ? damsById.get(r.dam_animal_id) : undefined;
      if (!r.dam_animal_id) return <span className="text-ink-500">Bought in</span>;
      return (
        <span className="flex flex-col">
          <span>{dam?.animal_name || dam?.animal_id || shortId(r.dam_animal_id)}</span>
          {dam?.primary_tag && <span className="text-helper-text text-ink-500">{dam.primary_tag}</span>}
        </span>
      );
    } },
    { key: 'breed', header: bi('Breed', 'ತಳಿ'), hideOnTablet: true, className: 'whitespace-nowrap', render: (r) => (r.dam_animal_id && damsById.get(r.dam_animal_id)?.breed_name) || '—' },
    { key: 'purchase_date', header: bi('Bought on', 'ಖರೀದಿ'), hideOnTablet: true, className: 'whitespace-nowrap', render: (r) => (r.source === 'Purchased' ? formatDate(r.purchase_date ?? null) : '—') },
    { key: 'cost', header: bi('Cost', 'ಬೆಲೆ'), align: 'right', hideOnTablet: true, render: (r) => (
      r.source !== 'Purchased' ? '—' : Number(r.purchase_cost ?? 0) === 0 ? 'Free' : formatCurrency(r.purchase_cost)
    ) },
    { key: 'remarks', header: bi('Remarks', 'ಸೂಚನೆ'), hideOnTablet: true, render: (r) => (
      r.remarks ? <span className="block max-w-[14rem] truncate" title={r.remarks}>{r.remarks}</span> : '—'
    ) },
  ],
  fields: [
    { name: 'source', label: bi('Born here or bought?', 'ಇಲ್ಲಿ ಜನನ / ಖರೀದಿ'), type: 'select', section: bi('Birth', 'ಜನನ'), required: isCreating, visibleWhen: isCreating,
      staticOptions: [{ value: 'BornInFarm', label: bi('Born on the farm', 'ಫಾರ್ಮ್‌ನಲ್ಲಿ ಜನನ') }, { value: 'Purchased', label: bi('Bought', 'ಖರೀದಿಸಲಾಗಿದೆ') }],
      helperText: 'A bought calf can also be recorded from Procurement & Finance → Purchases (Type = Calf).' },
    { name: 'dam_animal_id', label: bi('Dam (Mother)', 'ತಾಯಿ'), type: 'select', section: bi('Birth', 'ಜನನ'), required: isBornHere, visibleWhen: isBornHere, optionsKey: 'animals' },
    { name: 'purchase_date', label: bi('Bought On', 'ಖರೀದಿ ದಿನಾಂಕ'), type: 'date', section: bi('Birth', 'ಜನನ'), required: isBoughtCalf, visibleWhen: isBoughtCalf },
    { name: 'purchase_cost', label: bi('Cost (₹)', 'ಬೆಲೆ (ರೂ.)'), type: 'decimal', section: bi('Birth', 'ಜನನ'), required: isBoughtCalf, visibleWhen: isBoughtCalf, min: 0,
      helperText: 'Enter 0 if the calf came free.' },
    { name: 'supplier_id', label: bi('Bought From', 'ಯಾರಿಂದ'), type: 'select', section: bi('Birth', 'ಜನನ'), optional: true, visibleWhen: isBoughtCalf, optionsKey: 'suppliers' },
    { name: 'status', label: bi('Status', 'ಸ್ಥಿತಿ'), type: 'select', section: bi('Birth', 'ಜನನ'), required: true, staticOptions: CALF_STATUSES,
      helperText: 'Only calves "On the farm" are counted on the Dashboard.' },
    { name: 'sire_animal_id', label: bi('Sire (Father)', 'ತಂದೆ'), type: 'select', section: bi('Birth', 'ಜನನ'), optional: true, optionsKey: 'animals' },
    { name: 'calf_gender', label: bi('Calf Gender', 'ಕರುವಿನ ಲಿಂಗ'), type: 'select', section: bi('Birth', 'ಜನನ'), required: true, staticOptions: [
      { value: 'Male', label: bi('Male', 'ಗಂಡು') }, { value: 'Female', label: bi('Female', 'ಹೆಣ್ಣು') },
    ] },
    { name: 'birth_date', label: bi('Birth Date', 'ಜನನ ದಿನಾಂಕ'), type: 'date', section: bi('Birth', 'ಜನನ'), required: true },
    { name: 'calf_name', label: bi('Calf Name', 'ಕರುವಿನ ಹೆಸರು'), type: 'text', section: bi('Birth', 'ಜನನ'), optional: true },
    { name: 'calf_color', label: bi('Calf Color', 'ಕರುವಿನ ಬಣ್ಣ'), type: 'text', section: bi('Birth', 'ಜನನ'), optional: true },
    { name: 'calf_tag_no', label: bi('Calf Tag No', 'ಕರುವಿನ ಟ್ಯಾಗ್ ಸಂಖ್ಯೆ'), type: 'text', section: bi('Birth', 'ಜನನ'), optional: true,
      helperText: 'A simple label for now (e.g. a temporary tag) - not linked to Tags & QR until this calf is registered as its own Animal.' },
    { name: 'birth_weight_kg', label: bi('Birth Weight (kg)', 'ಜನನ ತೂಕ (ಕೆಜಿ)'), type: 'decimal', section: bi('Birth', 'ಜನನ'), optional: true, min: 0 },
    { name: 'birth_height_cm', label: bi('Birth Height (cm)', 'ಜನನ ಎತ್ತರ (ಸೆಂ.ಮೀ)'), type: 'decimal', section: bi('Birth', 'ಜನನ'), optional: true, min: 0 },
    { name: 'colostrum_quantity_l', label: bi('Colostrum Quantity (L)', 'ಗಿಣ್ಣುಹಾಲು ಪ್ರಮಾಣ (ಲೀ)'), type: 'decimal', section: bi('Colostrum', 'ಗಿಣ್ಣುಹಾಲು'), optional: true, min: 0 },
    { name: 'remarks', label: bi('Remarks', 'ಸೂಚನೆ'), type: 'textarea', section: bi('Notes', 'ಸೂಚನೆ'), optional: true,
      helperText: 'E.g. "1st lactation - return on 22-09-2026".' },
  ] as FieldConfig<any>[],
  };
}

// ---------------------------------------------------------------------------
// Milk Sales — milk sold OUT to a Partner/Vendor/Customer (added on
// request), the distribution-side mirror of Milk Sessions above. Shares the
// same Pending → Approved gate: nothing here counts toward the customer-
// wise report or the Dashboard until an Admin approves it.
// ---------------------------------------------------------------------------

function SaleApprovalCell({ sale }: { sale: MilkSale }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canApprove = can('Milk', 'Approve');
  const mutation = useMutation({
    mutationFn: (status: MilkCollectionApprovalStatus) => milkSalesApi.updateApproval(sale.id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milk-sales'] });
      // A rejected sale frees its linked session for another sale.
      queryClient.invalidateQueries({ queryKey: ['reference', 'milkSessions'] });
      queryClient.invalidateQueries({ queryKey: ['milk-sessions'] });
    },
  });

  if (sale.approval_status !== 'Pending' || !canApprove) {
    return <ILMSStatusBadge status={sale.approval_status} size="sm" />;
  }
  return (
    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
      <ProofPhoto attachmentId={sale.photo_attachment_id} required={sale.sale_category === 'DairyCollectionHub'} />
      <div className="flex items-center gap-2">
        <ILMSStatusBadge status={sale.approval_status} size="sm" />
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('Approved')} className="text-helper-text text-success-700 hover:underline disabled:opacity-50">
          Approve
        </button>
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('Rejected')} className="text-helper-text text-critical-600 hover:underline disabled:opacity-50">
          Reject
        </button>
      </div>
      {mutation.isError && <span className="text-helper-text text-critical-600">{getApiErrorMessage(mutation.error, 'Unable to update approval status.')}</span>}
    </div>
  );
}

const SALE_CATEGORIES: { value: MilkSaleCategory; label: string }[] = [
  { value: 'DairyCollectionHub', label: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ') },
  { value: 'Partner', label: bi('Sales to Partners', 'ಪಾಲುದಾರರಿಗೆ ಮಾರಾಟ') },
  { value: 'Customer', label: bi('Sales to Customers', 'ಗ್ರಾಹಕರಿಗೆ ಮಾರಾಟ') },
  { value: 'CalfConsumption', label: bi('Consumption for Calves', 'ಕರುಗಳಿಗೆ ಬಳಕೆ') },
  { value: 'OfficeConsumption', label: bi('Consumption for Office', 'ಕಚೇರಿ ಬಳಕೆ') },
];
const SALE_CATEGORY_LABEL = Object.fromEntries(SALE_CATEGORIES.map((c) => [c.value, c.label])) as Record<MilkSaleCategory, string>;

const isSoldToHub = (v: Record<string, unknown>) => v.sale_category === 'DairyCollectionHub';
const isSoldToParty = (v: Record<string, unknown>) => v.sale_category === 'Partner' || v.sale_category === 'Customer';
const isConsumption = (v: Record<string, unknown>) => v.sale_category === 'CalfConsumption' || v.sale_category === 'OfficeConsumption';
const isRealSale = (v: Record<string, unknown>) => !isConsumption(v);
// A photo is required before a Hub sale can be approved (added on request,
// same rule Milk Session's own photo already enforces) - only re-required
// when there truly isn't one yet (a milk session's own photo is optional). Not required
// on the other categories - there's no physical slip to verify there.
const needsNewHubPhoto = (v: Record<string, unknown>) => isSoldToHub(v) && !v.photo_attachment_id;

// The fields each category sends - everything else is explicitly cleared,
// so switching an existing sale's category never leaves a stale buyer.
function categoryPayload(v: any) {
  const cleared = { customer_id: null, dairy_collection_hub_id: null, member_number: null, person_name: null, milk_session_id: null, covers_shift: false };
  if (isSoldToHub(v)) {
    return {
      ...cleared, dairy_collection_hub_id: v.dairy_collection_hub_id, member_number: v.member_number || null,
      person_name: v.person_name || null,
      // A shift picked = this sale is for every cow's milk in that shift;
      // otherwise keep the one session an older sale was linked to.
      covers_shift: !!v.milk_shift, milk_session_id: v.milk_shift ? null : (v.milk_session_id || null),
    };
  }
  if (isSoldToParty(v)) return { ...cleared, customer_id: v.customer_id };
  return cleared;
}

// "2026-11-01|Morning" <-> the sale's own date + shift.
const shiftKey = (date: string, shift: string) => `${date}|${shift}`;
const splitShiftKey = (key: string) => {
  const [date, shift] = key.split('|');
  return { date, shift: shift as MilkSessionType };
};

// Milk Sale is the one distribution table (see app/models/dairy.py's
// MilkSale docstring): every litre of collected milk that leaves the herd -
// sold to a Hub, a Partner or a Customer, or consumed by the calves or the
// office - is one row here, so collection and distribution always reconcile.
function buildMilkSaleConfig(customersById: Map<string, Customer>, hubsById: Map<string, DairyCollectionHub>, defaultHubId: string): ModuleConfig<MilkSale> {
  const salePayload = (v: any) => ({
    sale_date: v.sale_date, shift: v.shift || undefined, sale_category: v.sale_category, ...categoryPayload(v),
    // The picked shift decides the sale's date and shift.
    ...(isSoldToHub(v) && v.milk_shift ? { sale_date: splitShiftKey(v.milk_shift).date, shift: splitShiftKey(v.milk_shift).shift } : {}),
    litres: Number(v.litres), fat_percent: isSoldToHub(v) && v.fat_percent !== '' && v.fat_percent != null ? Number(v.fat_percent) : undefined,
    // Consumption has no buyer and no revenue.
    rate_per_litre: isConsumption(v) ? 0 : Number(v.rate_per_litre), notes: v.notes || undefined,
  });
  return {
    title: bi('Milk Sales', 'ಹಾಲು ಮಾರಾಟ'), entityLabel: 'milk sale', module: 'Milk', queryKey: 'milk-sales', idKey: 'id',
    api: {
      list: milkSalesApi.list,
      create: async (v: any) => {
        const created = await milkSalesApi.create(salePayload(v) as any);
        // Same two-step create-then-attach pattern as Milk Session's own
        // photo - the sale must exist before an Attachment can reference
        // its id. Only the Hub category requires one.
        if (isSoldToHub(v) && v.photo_file) {
          try {
            const attachment = await attachmentsApi.upload(v.photo_file as File, 'MilkSale', created.id);
            return await milkSalesApi.setPhoto(created.id, attachment.id);
          } catch (err) {
            await milkSalesApi.remove(created.id).catch(() => {});
            throw new Error(getApiErrorMessage(err, 'The sale could not be saved — the photo failed to upload.'));
          }
        }
        return created;
      },
      update: async (id: string, v: any) => {
        let updated = await milkSalesApi.update(id, salePayload(v) as any);
        if (isSoldToHub(v) && v.photo_file) {
          try {
            const attachment = await attachmentsApi.upload(v.photo_file as File, 'MilkSale', id);
            updated = await milkSalesApi.update(id, { photo_attachment_id: attachment.id } as any);
          } catch (err) {
            throw new Error(getApiErrorMessage(err, 'The sale was saved, but the photo failed to upload — try again.'));
          }
        }
        return updated;
      },
      remove: milkSalesApi.remove,
    },
    canDelete: true,
    referenceKeys: ['customers', 'dairyCollectionHubs', 'userNames', 'milkShifts'], addLabel: 'Record Sale',
    // Date · Shift · Category, then Quantity · Fat % · Rate on one line each.
    sectionColumns: { [bi('Sale', 'ಮಾರಾಟ')]: 3 },
    searchPlaceholder: 'Search by sale code…',
    // Defaults match how most sales on this farm actually happen (on
    // request, carried over from what used to be the Milk Session form's
    // own defaults): sold to the default Dairy Collection Hub, the usual
    // member number and operator - still fully editable per entry. The
    // shift is the one going on now (Evening from 12 noon).
    defaultValues: () => ({
      sale_date: today(), shift: currentShift(), sale_category: 'DairyCollectionHub', dairy_collection_hub_id: defaultHubId,
      member_number: '0160', person_name: 'Shoalin',
    }) as unknown as Partial<MilkSale>,
    deriveEditValues: (row) => ({ milk_shift: row.covers_shift && row.shift ? shiftKey(row.sale_date, row.shift) : '' }),
    columns: [
      { key: 'date', header: bi('Date', 'ದಿನಾಂಕ'), primary: true, render: (r) => formatDate(r.sale_date) },
      { key: 'code', header: bi('Sale Code', 'ಮಾರಾಟ ಐಡಿ'), render: (r) => (
        <span>{r.sale_code ?? '—'}{r.is_subscription && <span className="ml-1.5 rounded-full bg-brand-green-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-green-700">Subscription</span>}</span>
      ) },
      { key: 'customer', header: bi('Sold To', 'ಯಾರಿಗೆ ಮಾರಾಟ'), render: (r) => (
        r.dairy_collection_hub_id ? (hubsById.get(r.dairy_collection_hub_id)?.name ?? shortId(r.dairy_collection_hub_id))
          : r.customer_id ? (customersById.get(r.customer_id)?.name ?? shortId(r.customer_id))
            : '—'
      ) },
      { key: 'shift', header: bi('Shift', 'ಶಿಫ್ಟ್'), render: (r) => (r.shift ? `${r.shift}${r.covers_shift ? ' · all cows' : ''}` : '—'), hideOnTablet: true },
      { key: 'type', header: bi('Category', 'ವರ್ಗ'), render: (r) => SALE_CATEGORY_LABEL[r.sale_category] ?? r.sale_category, hideOnTablet: true },
      { key: 'litres', header: bi('Litres', 'ಲೀಟರ್'), align: 'right', render: (r) => formatNumber(r.litres, 2) },
      { key: 'rate', header: bi('Rate (₹/L)', 'ದರ (ರೂ/ಲಿ)'), align: 'right', render: (r) => formatNumber(r.rate_per_litre, 2), hideOnTablet: true },
      { key: 'amount', header: bi('Amount (₹)', 'ಮೊತ್ತ (ರೂ.)'), align: 'right', render: (r) => (r.amount != null ? formatCurrency(r.amount) : '—') },
      { key: 'status', header: bi('Status', 'ಸ್ಥಿತಿ'), render: (r) => (
        <span className="flex flex-col gap-0.5">
          <SaleApprovalCell sale={r} />
          {r.hub_payment_id && <span className="text-[11px] font-medium text-success-700">Paid by hub</span>}
        </span>
      ) },
    ],
    fields: [
      { name: 'sale_date', label: bi('Date', 'ದಿನಾಂಕ'), type: 'date', section: bi('Sale', 'ಮಾರಾಟ'), required: true },
      { name: 'shift', label: bi('Shift', 'ಶಿಫ್ಟ್'), type: 'select', section: bi('Sale', 'ಮಾರಾಟ'), optional: true, staticOptions: [
        { value: 'Morning', label: bi('Morning', 'ಬೆಳಗ್ಗೆ') }, { value: 'Evening', label: bi('Evening', 'ಸಂಜೆ') }, { value: 'Third', label: bi('Third', 'ಮೂರನೇ') },
      ] },
      { name: 'sale_category', label: bi('Sales Category', 'ಮಾರಾಟ ವರ್ಗ'), type: 'select', section: bi('Sale', 'ಮಾರಾಟ'), required: true,
        staticOptions: SALE_CATEGORIES,
        helperText: 'Pick this first. Consumption = farm use, no buyer or amount.',
        // A party picked under one category isn't valid under another.
        onValueChange: () => ({ customer_id: '' }) },
      { name: 'customer_id', label: bi('Partner / Customer', 'ಪಾಲುದಾರ / ಗ್ರಾಹಕ'), type: 'select', optionsKey: 'customers', section: bi('Sale', 'ಮಾರಾಟ'),
        required: isSoldToParty, visibleWhen: isSoldToParty,
        // Partners only for "Sales to Partners"; Customers and Vendors for "Sales to Customers".
        filterOptions: (options, values) => options.filter((o) => (
          values.sale_category === 'Partner' ? o.meta?.partyType === 'Partner' : o.meta?.partyType !== 'Partner'
        )),
        helperText: 'Manage this list (and each one\'s default rate) from Procurement & Finance → Customers.',
        onValueChange: (value, values) => {
          const customer = customersById.get(value as string);
          // Only auto-fill when the Rate field is still untouched (blank) —
          // picking a different customer on an existing sale shouldn't
          // silently overwrite a rate someone already typed/edited.
          if (customer?.default_rate_per_litre != null && (values.rate_per_litre === '' || values.rate_per_litre == null)) {
            return { rate_per_litre: Number(customer.default_rate_per_litre) };
          }
          return undefined;
        } },
      { name: 'litres', label: bi('Quantity (L)', 'ಪ್ರಮಾಣ (ಲಿ)'), type: 'decimal', section: bi('Sale', 'ಮಾರಾಟ'), required: true, min: 0 },
      // Fat % only matters for the Hub - not for partners, customers or consumption.
      { name: 'fat_percent', label: bi('Fat %', 'ಕೊಬ್ಬು %'), type: 'decimal', section: bi('Sale', 'ಮಾರಾಟ'), optional: true, min: 0, visibleWhen: isSoldToHub },
      { name: 'rate_per_litre', label: bi('Rate (₹/L)', 'ದರ (ರೂ/ಲಿ)'), type: 'decimal', section: bi('Sale', 'ಮಾರಾಟ'),
        required: isRealSale, visibleWhen: isRealSale, min: 0,
        helperText: 'Amount = Quantity × Rate.' },
      { name: 'notes', label: bi('Notes', 'ಸೂಚನೆ'), type: 'textarea', section: bi('Notes', 'ಸೂಚನೆ'), optional: true },

      // Dairy Collection Hub, last — everything here now comes pre-filled
      // by default (see defaultValues above) and rarely needs touching.
      { name: 'dairy_collection_hub_id', label: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'), type: 'select', optionsKey: 'dairyCollectionHubs', section: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'),
        required: isSoldToHub, visibleWhen: isSoldToHub,
        helperText: 'Manage the list (and which one is default) from the Dairy Collection Hubs tab.' },
      { name: 'member_number', label: bi('Number', 'ಸಂಖ್ಯೆ'), type: 'text', section: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'),
        required: isSoldToHub, visibleWhen: isSoldToHub, helperText: 'The farmer/member number printed on the physical collection hub slip.' },
      { name: 'person_name', label: bi('Name of the Operator', 'ಹೆಸರು'), type: 'select', optionsKey: 'userNames', section: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'),
        required: isSoldToHub, visibleWhen: isSoldToHub,
        helperText: 'The helper/worker (an ILMS user) who delivered this milk to the collection hub.' },
      // Every cow's milk of one shift, added up - the sale is for all of it
      // (milk is recorded cow by cow, so picking sessions one by one made no sense).
      { name: 'milk_shift', label: bi('Milk of shift', 'ಶಿಫ್ಟ್‌ನ ಹಾಲು'), type: 'select', optionsKey: 'milkShifts', section: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'),
        visibleWhen: isSoldToHub, optional: true,
        // Shifts with milk not sold yet - plus the one this sale already holds (when editing).
        filterOptions: (options, values) => options.filter((o) => (o.meta?.unsold as number) > 0 || (!!values.id && o.meta?.shiftSaleId === values.id)),
        onValueChange: (value) => {
          if (!value) return undefined;
          const { date, shift } = splitShiftKey(value as string);
          return { sale_date: date, shift };
        },
        helperText: 'Pick the shift this hub slip is for - all the cows milked in that shift are sold through this one sale. Only shifts with milk not yet sold are listed. Left blank, the sale is matched to the same date and shift automatically.' },
      { name: 'photo_file', label: bi('Photo', 'ಫೋಟೋ'), type: 'file', section: bi('Dairy Collection Hub', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರ'),
        required: needsNewHubPhoto, visibleWhen: isSoldToHub, existingAttachmentKey: 'photo_attachment_id',
        helperText: 'A photo of the physical collection hub slip is required before this sale can be approved.' },
    ] as FieldConfig<any>[],
  };
}

// One row per still-unresolved subscription day (computed fresh every time
// this tab loads - no scheduler anywhere in this app, see dairy.py's
// get_pending_subscription_confirmations). "Yes" records an already-
// Approved sale at the usual quantity/rate right away; "No" just moves on
// to the next day - neither needs a separate admin approval step.
function PendingSubscriptionConfirmations() {
  const queryClient = useQueryClient();
  const pendingQuery = useQuery({ queryKey: ['milk-sales-pending-confirmations'], queryFn: () => milkSalesApi.pendingConfirmations() });
  const mutation = useMutation({
    mutationFn: (payload: MilkSaleConfirmRequest) => milkSalesApi.confirm(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milk-sales-pending-confirmations'] });
      queryClient.invalidateQueries({ queryKey: ['milk-sales'] });
    },
  });

  const pending = pendingQuery.data ?? [];
  if (!pendingQuery.isLoading && pending.length === 0) return null;

  return (
    <ILMSCard
      title={bi('Pending Subscription Confirmations', 'ಬಾಕಿ ಇರುವ ಚಂದಾದಾರಿಕೆ ದೃಢೀಕರಣಗಳು')}
      subtitle="Did these subscribed customers get milk on these days? Yes records the usual quantity/rate immediately — no separate approval needed."
    >
      {pendingQuery.isLoading ? (
        <p className="text-secondary text-ink-500">Checking subscriptions…</p>
      ) : (
        <div className="flex flex-col divide-y divide-ink-300/10">
          {pending.map((p) => {
            const key = `${p.customer_id}-${p.sale_date}`;
            const busy = mutation.isPending && mutation.variables?.customer_id === p.customer_id && mutation.variables?.sale_date === p.sale_date;
            return (
              <div key={key} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div>
                  <p className="text-body font-medium text-ink-900">{p.customer_name}</p>
                  <p className="text-helper-text text-ink-500">
                    {formatDate(p.sale_date)} —{' '}
                    {p.shift === 'Both'
                      ? `Morning ${formatNumber(p.morning_litres, 2)}L + Evening ${formatNumber(p.evening_litres, 2)}L (${formatNumber(p.litres, 2)}L total)`
                      : `${p.shift} ${formatNumber(p.litres, 2)}L`}
                    {' '}@ ₹{formatNumber(p.rate_per_litre, 2)}/L ({formatCurrency(Number(p.litres) * Number(p.rate_per_litre))})
                  </p>
                </div>
                <div className="flex gap-2">
                  <ILMSButton
                    size="sm" variant="ghost" disabled={mutation.isPending} loading={busy && mutation.variables?.confirmed === false}
                    onClick={() => mutation.mutate({ customer_id: p.customer_id, sale_date: p.sale_date, confirmed: false })}
                  >
                    No
                  </ILMSButton>
                  <ILMSButton
                    size="sm" disabled={mutation.isPending} loading={busy && mutation.variables?.confirmed === true}
                    onClick={() => mutation.mutate({ customer_id: p.customer_id, sale_date: p.sale_date, confirmed: true })}
                  >
                    Yes
                  </ILMSButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {mutation.isError && <ILMSAlert tone="critical" className="mt-3">{getApiErrorMessage(mutation.error, 'Unable to record this confirmation.')}</ILMSAlert>}
    </ILMSCard>
  );
}

function MilkSalesTab() {
  const customersQuery = useQuery({ queryKey: ['customers-full-for-milk-sales'], queryFn: () => customersApi.list({ page_size: 500 }) });
  const customersById = React.useMemo(() => {
    const map = new Map<string, Customer>();
    (customersQuery.data?.items ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [customersQuery.data]);
  // A dedicated fetch (not useReferenceData, which only carries {value,
  // label} pairs) - the Add-sale default needs to know WHICH hub is
  // flagged is_default, not just the list of names, same as the old
  // Milk Session form used to before the Hub section moved here.
  const hubsQuery = useQuery({ queryKey: ['dairy-collection-hubs-default'], queryFn: () => dairyCollectionHubsApi.list({ page_size: 200 }) });
  const hubsById = React.useMemo(() => {
    const map = new Map<string, DairyCollectionHub>();
    (hubsQuery.data?.items ?? []).forEach((h) => map.set(h.id, h));
    return map;
  }, [hubsQuery.data]);
  const defaultHubId = hubsQuery.data?.items.find((h) => h.is_default)?.id ?? '';
  return (
    <div className="flex flex-col gap-4">
      <PendingSubscriptionConfirmations />
      <ResourceModule config={buildMilkSaleConfig(customersById, hubsById, defaultHubId)} />
    </div>
  );
}

// Day sheet (one row per cow per day, Morning and Evening side by side - on
// request, once milk started being recorded cow by cow) or every entry as
// recorded, where entries are edited, deleted and approved one by one.
interface CorrectionRow {
  id: string; session_code?: string | null; production_date: string; session_type: string; animal_id?: string | null; group_animal_ids?: string[] | null;
  litres: string; fat_percent?: string | null; approval_status: string;
  correction_request: { litres: number; fat_percent?: number | null; snf_percent?: number | null; reason: string; requested_by_name?: string; requested_at?: string };
}

// Corrections agents asked for (an approved entry, or past their own 2
// corrections / 24 hours) - approve applies the new figures, reject keeps the old.
function CorrectionRequestsPanel() {
  const qc = useQueryClient();
  const { data: ref } = useReferenceData(['animals']);
  const list = useQuery({ queryKey: ['milk-correction-requests'], queryFn: () => apiClient.get<CorrectionRow[]>('/milk-sessions/correction-requests').then((r) => r.data), refetchInterval: 60_000 });
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) => apiClient.post(`/milk-sessions/${id}/correction-request/decide`, { approve }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['milk-correction-requests'] }); qc.invalidateQueries({ queryKey: ['milk-sessions'] }); qc.invalidateQueries({ queryKey: ['milk-shifts'] }); },
  });
  const rows = list.data ?? [];
  if (!rows.length) return null;
  const label = (id?: string | null) => ref.animals.find((o) => o.value === id)?.label ?? 'Cow';
  return (
    <ILMSCard title={`Correction requests (${rows.length})`} subtitle="Agents asking to change an entry. Approve uses the new figures; Reject keeps the entry as it is.">
      {decide.isError && <ILMSAlert tone="critical">{getApiErrorMessage(decide.error)}</ILMSAlert>}
      <ul className="flex flex-col divide-y divide-ink-300/20">
        {rows.map((r) => {
          const c = r.correction_request;
          return (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2.5 text-secondary">
              <span className="min-w-[14rem] flex-1">
                <span className="font-medium">{r.animal_id ? label(r.animal_id) : `Batch of ${r.group_animal_ids?.length ?? 0}`}</span>
                <span className="text-ink-500"> · {formatDate(r.production_date)} {r.session_type} · {r.session_code} ({r.approval_status})</span>
                <span className="block">
                  <b>{formatNumber(r.litres, 2)} L → {formatNumber(c.litres, 2)} L</b>
                  {c.fat_percent != null && <> · fat {r.fat_percent != null ? formatNumber(r.fat_percent, 2) : '—'} → {formatNumber(c.fat_percent, 2)}%</>}
                </span>
                <span className="block text-helper-text text-ink-500">"{c.reason}" — {c.requested_by_name ?? 'agent'}</span>
              </span>
              <span className="flex gap-2">
                <ILMSButton size="sm" loading={decide.isPending && decide.variables?.id === r.id && decide.variables.approve} onClick={() => decide.mutate({ id: r.id, approve: true })}>Approve</ILMSButton>
                <ILMSButton size="sm" variant="outline" onClick={() => decide.mutate({ id: r.id, approve: false })}>Reject</ILMSButton>
              </span>
            </li>
          );
        })}
      </ul>
    </ILMSCard>
  );
}

function MilkSessionsTab() {
  const { can } = useAuth();
  const [view, setView] = React.useState<'sheet' | 'entries'>('sheet');
  // "Record Session" on the day sheet opens the entries list straight into Add.
  const [openAdd, setOpenAdd] = React.useState(false);
  const tabBtn = (id: 'sheet' | 'entries', label: string) => (
    <button
      type="button"
      onClick={() => { setView(id); setOpenAdd(false); }}
      className={`h-9 rounded-md px-3 text-sm font-medium ${view === id ? 'bg-brand-green-700 text-white' : 'text-ink-600 hover:bg-surface-subtle'}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-col gap-4">
      {can('Milk', 'Approve') && <CorrectionRequestsPanel />}
      <MilkShiftsPanel />
      <div className="flex w-fit gap-1 rounded-lg border border-ink-300/20 bg-white p-1">
        {tabBtn('sheet', bi('Day sheet (per cow)', 'ದಿನದ ಹಾಳೆ (ಪ್ರತಿ ಹಸು)'))}
        {tabBtn('entries', bi('All entries', 'ಎಲ್ಲಾ ನಮೂದುಗಳು'))}
      </div>
      {view === 'sheet'
        ? <MilkDaySheet canRecord={can('Milk', 'Create')} onRecord={() => { setOpenAdd(true); setView('entries'); }} />
        : <ResourceModule config={buildMilkConfig()} autoOpenAdd={openAdd ? {} : undefined} />}
    </div>
  );
}

function CalfRecordsTab() {
  // A separate, full-detail fetch (not the shared useReferenceData cache,
  // which only carries {value,label} pairs) - the Mother-derived columns
  // need breed/location/farm/purchase-date/tag, all already on AnimalRead.
  const animalsQuery = useQuery({ queryKey: ['animals-full-for-calf-records'], queryFn: () => animalsApi.list({ page_size: 1000 }) });
  const damsById = React.useMemo(() => {
    const map = new Map<string, Animal>();
    (animalsQuery.data?.items ?? []).forEach((a) => map.set(a.id, a));
    return map;
  }, [animalsQuery.data]);
  return <ResourceModule config={buildCalfRecordsConfig(damsById)} />;
}

export default function DairyPage() {
  const [searchParams] = useSearchParams();
  return (
    <TabbedPage
      defaultTab={searchParams.get('tab') ?? undefined}
      title={bi('Dairy', 'ಹೈನುಗಾರಿಕೆ')}
      subtitle="Milk sessions (collection centre delivery included), daily aggregates, lactation cycles and cow-specific profiles."
      tabs={[
        { id: 'sessions', label: bi('Milk Sessions', 'ಹಾಲು ಅಧಿವೇಶನಗಳು'), render: () => <MilkSessionsTab /> },
        { id: 'sales', label: bi('Milk Sales', 'ಹಾಲು ಮಾರಾಟ'), render: () => <MilkSalesTab /> },
        { id: 'collection-hubs', label: bi('Dairy Collection Hubs', 'ಡೈರಿ ಸಂಗ್ರಹ ಕೇಂದ್ರಗಳು'), render: () => <ResourceModule config={dairyCollectionHubsConfig} /> },        { id: 'summary', label: bi('Daily Summary', 'ದೈನಂದಿನ ಸಾರಾಂಶ'), render: () => <ResourceModule config={summaryConfig} /> },
        { id: 'lactations', label: bi('Lactations', 'ಹಾಲು ಅವಧಿಗಳು'), render: () => <ResourceModule config={lactationsConfig} /> },
        { id: 'cow-profiles', label: bi('Cow Profiles', 'ಹಸು ಪ್ರೊಫೈಲ್‌ಗಳು'), render: () => <ResourceModule config={cowProfilesConfig} /> },
        { id: 'calf-records', label: bi('Calf Records', 'ಕರು ದಾಖಲೆಗಳು'), render: () => <CalfRecordsTab /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Dairy', buildMilkConfig(), buildMilkSaleConfig(new Map(), new Map(), ''), dairyCollectionHubsConfig, lactationsConfig, cowProfilesConfig, buildCalfRecordsConfig(new Map()));
